import Icon from '@/components/Icon';
import { useEpvFamilies } from '@/hooks/api';
import { Column, Row, Table } from '@/styles/common';
import axios from '@/utils/axios';
import { formatNum, isInRange } from '@/utils/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

interface MatchingCapacityProps {
	data: TMatchingData | undefined;
	yearRange: [number, number];
	processStep: TValue;
	regions: TValue[];
	displays: TValue[];
	by: TValue;
	selectedEpvFamilies: TValue[];
	selectedProcessStep: TProcessStep | undefined;
	demandRepartition: TValue;
	filterBySalesRegionOrEpv: boolean;
}

const MatchingCapacity: React.FC<MatchingCapacityProps> = ({
	data,
	yearRange,
	processStep,
	regions,
	displays,
	by,
	selectedEpvFamilies,
	selectedProcessStep,
	demandRepartition,
	filterBySalesRegionOrEpv,
}) => {
	const isGroup = by === 'group';
	const isGlobalRegion = ['global regions'].includes(by as string);
	const isProductionRegion = by === 'production region';
	const isPlant = by === 'plant';

	const { data: tree } = useQuery(
		['capacity_tree'],
		() => axios.get<TCapacityTree>('/capacity_tree'),
		{
			select: ({ data }) => data,
		}
	);

	const { data: epvFamilies } = useEpvFamilies();
	const ycwDryingEpvFamilyIds = epvFamilies
		?.filter((epvFamily) => epvFamily.ycw_cream !== 0 && epvFamily.drying !== 0)
		.map((epvFamily) => epvFamily.epv_family_lvl2_id);

	const [regionsExpanded, setRegionsExpanded] = useState(false);
	const [productionRegionsExpanded, setProductionRegionsExpanded] = useState<
		Record<string, boolean>
	>({});
	const [plantExpanded, setPlantExpanded] = useState<Record<string, boolean>>(
		{}
	);

	const [ycwRegionsExpanded, setYcwRegionsExpanded] = useState(false);
	const [ycwProductionRegionsExpanded, setYcwProductionRegionsExpanded] =
		useState<Record<string, boolean>>({});
	const [ycwPlantExpanded, setYcwPlantExpanded] = useState<
		Record<string, boolean>
	>({});

	const [showDemandDetailsGroup, setShowDemandDetailsGroup] = useState(false);
	const [showDemandDetailsGlobalRegion, setShowDemandDetailsGlobalRegion] =
		useState<Record<string, boolean>>({});
	const [
		showDemandDetailsProductionRegion,
		setShowDemandDetailsProductionRegion,
	] = useState<Record<string, boolean>>({});
	const [showDemandDetailsPlant, setShowDemandDetailsPlant] = useState<
		Record<string, boolean>
	>({});

	const getProductionRegionsByRegion = (region: string) => {
		if (!tree) return [];
		const productionRegions = tree
			.filter((node) => {
				if (isProductionRegion && displays.length > 0) {
					return displays.includes(node.production_region);
				}
				return true;
			})
			.filter((node) => node.global_region === region)
			.map((node) => node.production_region);
		return [...new Set(productionRegions)];
	};

	const getPlantsByRegionAndProductionRegion = (
		region: string,
		productionRegion: string
	) => {
		if (!tree) return [];
		const plants = tree
			.filter((node) => {
				if (isPlant && displays.length > 0) {
					return displays.includes(node.plant_code);
				}
				return true;
			})
			.filter(
				(node) =>
					node.global_region === region &&
					node.production_region === productionRegion
			)
			.map((node) => node.plant_code);
		return [...new Set(plants)];
	};

	const years = useMemo(() => {
		return Array.from(
			{ length: yearRange[1] - yearRange[0] + 1 },
			(_, i) => yearRange[0] + i
		);
	}, [data]);

	useEffect(() => {
		if (by === 'global regions') {
			setRegionsExpanded(true);
		}
		if (by === 'group') {
			setRegionsExpanded(false);
		}
	}, [by]);

	const getReplacedGlobalProductionRegion = (plant: string) => {
		if (!tree) return undefined;
		const node = tree.find((node) => node.plant_code === plant);
		if (!node) return undefined;
		return {
			g: node.global_region,
			p: node.production_region,
		};
	};

	const getDemandDetailByYearByGlobalRegion = (
		region: string | null,
		production?: string | null,
		plant?: string | null
	) => {
		if (!data) return {};

		const demandByYear: Record<string, number[]> = {};
		const key = `${processStep}_demand`;
		for (const d of data.demand_detailed) {
			let {
				year,
				global_region,
				production_region,
				matching,
				epv_family_lvl2_id,
				epv_family_lvl2,
				sales_region,
			} = d;
			if (!isInRange(year, yearRange)) continue;
			const demand = d[key as keyof typeof d] as number;

			if (isGlobalRegion) {
				const replaced = getReplacedGlobalProductionRegion(matching);
				if (replaced !== undefined) {
					global_region = replaced.g;
				}
			}
			const filterByGroup = !region && !production && !plant;
			const filterByGlobalRegion = region && !production && !plant;
			const filterByProductionRegion = region && production && !plant;
			const filterByPlant = region && production && plant;
			const filterByEpv = filterBySalesRegionOrEpv === true;

			let k = sales_region;

			if (filterByEpv) k = epv_family_lvl2;

			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!selectedEpvFamilies.includes(epv_family_lvl2_id)
			)
				continue;

			if (displays) {
				if (isGlobalRegion && !displays.includes(global_region)) continue;
				if (isProductionRegion && !displays.includes(production_region))
					continue;
				if (isPlant && !displays.includes(matching)) continue;
			}

			if (
				(filterByPlant &&
					plant === matching &&
					production === production_region) ||
				(filterByProductionRegion && production === production_region) ||
				(filterByGlobalRegion && region === global_region) ||
				filterByGroup
			) {
				if (!demandByYear[k]) {
					demandByYear[k] = [];
				}
				demandByYear[k][year] = (demandByYear[k][year] || 0) + demand;
			}
		}

		return demandByYear;
	};

	const getDemandByYearByGlobalRegion = (
		region: string | null,
		production?: string | null,
		plant?: string | null
	) => {
		if (!data) return {};

		const demandByYear: Record<number, number> = {};
		const key = `${processStep}_demand`;
		for (const d of data.demand_fulfilled) {
			let {
				year,
				global_region,
				production_region,
				matching,
				epv_family_lvl2_id,
			} = d;
			if (!isInRange(year, yearRange)) continue;
			const demand = d[key as keyof typeof d] as number;
			if (demandRepartition === 'history') {
				const replaced = getReplacedGlobalProductionRegion(matching);
				if (replaced !== undefined) {
					global_region = replaced.g;
					production_region = replaced.p;
				}
			}

			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!selectedEpvFamilies.includes(epv_family_lvl2_id)
			)
				continue;

			if (displays) {
				if (isGlobalRegion && !displays.includes(global_region)) continue;
				if (isProductionRegion && !displays.includes(production_region))
					continue;
				if (isPlant && !displays.includes(matching)) continue;
			}

			if (
				(region &&
					production &&
					plant &&
					region === global_region &&
					production === production_region &&
					plant === matching) ||
				(region &&
					production &&
					!plant &&
					region === global_region &&
					production === production_region) ||
				(region && !production && !plant && global_region === region) ||
				(!region && !production && !plant)
			) {
				if (demandByYear[year]) {
					demandByYear[year] += demand;
				} else {
					demandByYear[year] = demand;
				}
			}
		}

		return demandByYear;
	};

	const getCapacityByYearByGlobalRegion = (
		region: string | null,
		production?: string | null,
		plant?: string | null
	) => {
		if (!data) return {};

		const capacityByYear: Record<number, number> = {};
		for (const d of data.capacity_used) {
			const {
				year,
				global_region,
				capacity,
				capacity_type,
				production_region,
				matching,
				epv_family_lvl2_ids,
			} = d;
			if (capacity_type !== processStep) continue;
			if (!isInRange(year, yearRange)) continue;
			if (capacity_type !== processStep) continue;

			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!epv_family_lvl2_ids.some((id) => selectedEpvFamilies.includes(id))
			)
				continue;
			if (displays) {
				if (isGlobalRegion && !displays.includes(global_region)) continue;
				if (isProductionRegion && !displays.includes(production_region))
					continue;
				if (isPlant && !displays.includes(matching)) continue;
			}

			if (
				(region &&
					production &&
					plant &&
					region === global_region &&
					production === production_region &&
					plant === matching) ||
				(region &&
					production &&
					!plant &&
					region === global_region &&
					production === production_region) ||
				(region && !production && !plant && global_region === region) ||
				(!region && !production && !plant)
			) {
				if (capacityByYear[year]) {
					capacityByYear[year] += capacity;
				} else {
					capacityByYear[year] = capacity;
				}
			}
		}
		return capacityByYear;
	};

	const getDryingCapacity = (
		region: string | null,
		production?: string | null,
		plant?: string | null
	) => {
		if (!data) return {};

		const capacityByYear: Record<number, number> = {};
		for (const d of data.capacity_used) {
			const {
				year,
				global_region,
				capacity,
				capacity_type,
				production_region,
				matching,
				epv_family_lvl2_ids,
			} = d;
			if (capacity_type !== 'drying') continue;
			if (
				!ycwDryingEpvFamilyIds?.some((id) => epv_family_lvl2_ids.includes(id))
			)
				continue;

			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!epv_family_lvl2_ids.some((id) => selectedEpvFamilies.includes(id))
			)
				continue;

			if (!isInRange(year, yearRange)) continue;

			if (displays) {
				if (isGlobalRegion && !displays.includes(global_region)) continue;
				if (isProductionRegion && !displays.includes(production_region))
					continue;
				if (isPlant && !displays.includes(matching)) continue;
			}

			if (
				(region &&
					production &&
					plant &&
					region === global_region &&
					production === production_region &&
					plant === matching) ||
				(region &&
					production &&
					!plant &&
					region === global_region &&
					production === production_region) ||
				(region && !production && !plant && global_region === region) ||
				(!region && !production && !plant)
			) {
				if (capacityByYear[year]) {
					capacityByYear[year] += capacity;
				} else {
					capacityByYear[year] = capacity;
				}
			}
		}
		return capacityByYear;
	};

	const getColor = (diff: number) =>
		diff >= 0.05 ? 'green' : diff >= 0.01 && diff < 0.05 ? 'orange' : 'red';

	const isEmpty = (data: Record<number, number> | number[]) => {
		const predicate = (v: number) => formatNum({ value: v }) === '0';
		if (Array.isArray(data)) return data.every(predicate);

		return Object.values(data).every(predicate);
	};

	const detailsCount = 4;
	const capacityDisplay =
		processStep === 'ycw_cream' ? 'Generation' : 'Capacity';

	const delta = (capacity: number, demand: number) => {
		const diff = capacity - demand;
		const delta = demand === 0 ? 0 : (diff / demand) * 100;

		return formatNum({
			value: Math.abs(diff / demand) === 1 ? Math.abs(delta) : delta || 0,
		});
	};

	if (!data || !processStep) return null;

	return (
		<Column className='full'>
			<Table>
				<thead>
					<tr>
						<th></th>
						<th></th>
						{years.map((year) => (
							<th key={year}>{year}</th>
						))}
					</tr>
				</thead>
				<tbody>
					<tr>
						<td
							rowSpan={
								(isGroup && showDemandDetailsGroup
									? Object.entries(
											getDemandDetailByYearByGlobalRegion(null)
									  ).filter(([_, value]) => !isEmpty(value)).length
									: 0) + detailsCount
							}>
							<Row center className='full'>
								<span>Group</span>
								<span
									onClick={() => {
										setRegionsExpanded((_) => !_);
									}}
									style={{
										cursor: 'pointer',
									}}>
									{!isGroup && (
										<Icon
											icon='caret-right'
											style={{
												transition: 'transform 0.2s',
												transform: regionsExpanded
													? 'rotate(90deg)'
													: 'rotate(0deg)',
											}}
										/>
									)}
								</span>
							</Row>
						</td>
						<td>{capacityDisplay}</td>
						{years.map((year, i) => {
							const data = getCapacityByYearByGlobalRegion(null);
							return (
								<td key={`capa-group-${year}-${i}`}>
									{formatNum({
										value: data[year] || 0,
									})}
								</td>
							);
						})}
					</tr>
					<tr>
						<td>
							<Row center className='full'>
								<span>Demand</span>
								{isGroup && (
									<span
										onClick={() => {
											setShowDemandDetailsGroup((_) => !_);
										}}
										style={{
											cursor: 'pointer',
										}}>
										<Icon
											icon='caret-right'
											style={{
												transition: 'transform 0.2s',
												transform: showDemandDetailsGroup
													? 'rotate(90deg)'
													: 'rotate(0deg)',
											}}
										/>
									</span>
								)}
							</Row>
						</td>
						{years.map((year, i) => {
							const data = getDemandByYearByGlobalRegion(null);
							return (
								<td key={`demande-group-${year}-${i}`}>
									{formatNum({
										value: data[year] || 0,
									})}
								</td>
							);
						})}
					</tr>

					{isGroup &&
						showDemandDetailsGroup &&
						Object.entries(getDemandDetailByYearByGlobalRegion(null)).map(
							([key, value]) => {
								return isEmpty(value) ? null : (
									<tr
										style={{
											color: 'coral',
										}}>
										<td>{key}</td>
										{years.map((year, i) => (
											<td key={`demande-group-${year}-${i}`}>
												{formatNum({
													value: value[year] || 0,
												})}
											</td>
										))}
									</tr>
								);
							}
						)}
					<tr>
						<td>Remaining capacity</td>
						{years.map((year, i) => {
							const demande = getDemandByYearByGlobalRegion(null)[year];
							const capa = getCapacityByYearByGlobalRegion(null)[year];
							const diff = (capa ?? 0) - (demande ?? 0);
							return (
								<td
									key={`remaining-capa-group-${year}-${i}`}
									style={{
										color: getColor(diff / demande),
									}}>
									{formatNum({
										value: diff || 0,
									})}
								</td>
							);
						})}
					</tr>
					<tr>
						<td>Delta %</td>
						{years.map((year, i) => {
							const demande = getDemandByYearByGlobalRegion(null)[year] ?? 0;
							const capa = getCapacityByYearByGlobalRegion(null)[year] ?? 0;
							const diff = capa - demande;

							return (
								<td
									key={`delta-group-${year}-${i}`}
									style={{
										color: getColor(diff / demande),
									}}>
									{delta(capa, demande)}%
								</td>
							);
						})}
					</tr>
					{!isGroup &&
						regionsExpanded &&
						regions.map((region, regionIndex) => {
							const r = region as string;
							const isProductionRegionExpanded = productionRegionsExpanded[r];
							const demande = getDemandByYearByGlobalRegion(r);
							const capa = getCapacityByYearByGlobalRegion(r);

							return isEmpty(demande) && isEmpty(capa) ? null : (
								<React.Fragment key={`${region}-${regionIndex}`}>
									<tr>
										<td
											rowSpan={
												(isGlobalRegion && showDemandDetailsGlobalRegion[r]
													? Object.entries(
															getDemandDetailByYearByGlobalRegion(r)
													  ).filter(([_, value]) => !isEmpty(value)).length
													: 0) + detailsCount
											}
											style={{
												position: 'relative',
												border: 'none',
											}}>
											<Row
												center
												className='full'
												style={{
													border: '1px solid #ccc',
													borderRight: 'none',
													borderTop:
														regionIndex === 0 ? 'none' : '1px solid #ccc',
													borderBottom: '1px solid #ccc',
													position: 'absolute',
													top: 0,
													right: 0,
													bottom: 0,
													width: '190px',
												}}>
												<span>{region as string}</span>
												<span
													onClick={() => {
														setProductionRegionsExpanded((_) => ({
															..._,
															[r]: !_[r],
														}));
													}}
													style={{
														cursor: 'pointer',
													}}>
													{!isGlobalRegion && (
														<Icon
															icon='caret-right'
															style={{
																transition: 'transform 0.2s',
																transform: isProductionRegionExpanded
																	? 'rotate(90deg)'
																	: 'rotate(0deg)',
															}}
														/>
													)}
												</span>
											</Row>
										</td>
										<td>{capacityDisplay}</td>
										{years.map((year, i) => {
											return (
												<td key={`capa-${region}-${year}-${i}`}>
													{formatNum({
														value: capa[year] || 0,
													})}
												</td>
											);
										})}
									</tr>
									<tr>
										<td>
											<Row center className='full'>
												<span>Demand</span>
												{isGlobalRegion && (
													<span
														onClick={() => {
															setShowDemandDetailsGlobalRegion((_) => ({
																..._,
																[r]: !_[r],
															}));
														}}
														style={{
															cursor: 'pointer',
														}}>
														<Icon
															icon='caret-right'
															style={{
																transition: 'transform 0.2s',
																transform: showDemandDetailsGlobalRegion[r]
																	? 'rotate(90deg)'
																	: 'rotate(0deg)',
															}}
														/>
													</span>
												)}
											</Row>
										</td>
										{years.map((year, i) => (
											<td key={`demande-${region}-${year}-${i}`}>
												{formatNum({
													value: demande[year] || 0,
												})}
											</td>
										))}
									</tr>

									{isGlobalRegion &&
										showDemandDetailsGlobalRegion[r] &&
										Object.entries(getDemandDetailByYearByGlobalRegion(r)).map(
											([key, value]) => {
												return isEmpty(value) ? null : (
													<tr
														style={{
															color: 'coral',
														}}>
														<td>{key}</td>
														{years.map((year, i) => (
															<td key={`demande-group-${year}-${i}`}>
																{formatNum({
																	value: value[year] || 0,
																})}
															</td>
														))}
													</tr>
												);
											}
										)}
									<tr>
										<td>Remaining capacity</td>
										{years.map((year, i) => {
											const d = demande[year] ?? 0;
											const c = capa[year] ?? 0;
											const diff = c - d;
											return (
												<td
													key={`remaining-capa-${region}-${year}-${i}`}
													style={{
														color: getColor(diff / d),
													}}>
													{formatNum({
														value: diff || 0,
													})}
												</td>
											);
										})}
									</tr>
									<tr>
										<td>Delta %</td>
										{years.map((year, i) => {
											const d = demande[year] ?? 0;
											const c = capa[year] ?? 0;
											const diff = c - d;

											return (
												<td
													key={`delta-group-${year}-${i}`}
													style={{
														color: getColor(diff / d),
													}}>
													{delta(c, d)}%
												</td>
											);
										})}
									</tr>
									{!isGlobalRegion &&
										productionRegionsExpanded[region as string] &&
										getProductionRegionsByRegion(r).map(
											(productionRegion, prodIndex) => {
												const isPlantExpanded = plantExpanded[productionRegion];
												const demande = getDemandByYearByGlobalRegion(
													r,
													productionRegion
												);
												const capa = getCapacityByYearByGlobalRegion(
													r,
													productionRegion
												);
												return isEmpty(demande) && isEmpty(capa) ? null : (
													<React.Fragment key={`${region}-${prodIndex}`}>
														<tr
															style={{
																background: 'none',
															}}>
															<td
																rowSpan={
																	(isProductionRegion &&
																	showDemandDetailsProductionRegion[
																		productionRegion
																	]
																		? Object.entries(
																				getDemandDetailByYearByGlobalRegion(
																					r,
																					productionRegion
																				)
																		  ).filter(([_, value]) => !isEmpty(value))
																				.length
																		: 0) + detailsCount
																}
																style={{
																	position: 'relative',
																	border: 'none',
																}}>
																<Row
																	center
																	className='full'
																	style={{
																		border: '1px solid #ccc',
																		borderRight: 'none',
																		borderTop:
																			prodIndex === 0
																				? 'none'
																				: '1px solid #ccc',
																		borderBottom: '1px solid #ccc',
																		position: 'absolute',
																		top: 0,
																		right: 0,
																		bottom: 0,
																		width: '180px',
																	}}>
																	<span>{productionRegion}</span>
																	<span
																		onClick={() => {
																			setPlantExpanded((_) => ({
																				..._,
																				[productionRegion]:
																					!_[productionRegion],
																			}));
																		}}
																		style={{
																			cursor: 'pointer',
																		}}>
																		{!isProductionRegion && (
																			<Icon
																				icon='caret-right'
																				style={{
																					transition: 'transform 0.2s',
																					transform: isPlantExpanded
																						? 'rotate(90deg)'
																						: 'rotate(0deg)',
																				}}
																			/>
																		)}
																	</span>
																</Row>
															</td>
															<td>{capacityDisplay}</td>
															{years.map((year, i) => (
																<td key={`capa-${region}-${year}-${i}`}>
																	{formatNum({
																		value: capa[year] || 0,
																	})}
																</td>
															))}
														</tr>
														<tr>
															<td>
																<Row center className='full'>
																	<span>Demand</span>
																	{isProductionRegion && (
																		<span
																			onClick={() => {
																				setShowDemandDetailsProductionRegion(
																					(_) => ({
																						..._,
																						[productionRegion]:
																							!_[productionRegion],
																					})
																				);
																			}}
																			style={{
																				cursor: 'pointer',
																			}}>
																			<Icon
																				icon='caret-right'
																				style={{
																					transition: 'transform 0.2s',
																					transform:
																						showDemandDetailsProductionRegion[
																							productionRegion
																						]
																							? 'rotate(90deg)'
																							: 'rotate(0deg)',
																				}}
																			/>
																		</span>
																	)}
																</Row>
															</td>
															{years.map((year, i) => (
																<td key={`demande-${region}-${year}-${i}`}>
																	{formatNum({
																		value: demande[year] || 0,
																	})}
																</td>
															))}
														</tr>
														{isProductionRegion &&
															showDemandDetailsProductionRegion[
																productionRegion
															] &&
															Object.entries(
																getDemandDetailByYearByGlobalRegion(
																	r,
																	productionRegion
																)
															).map(([key, value]) => {
																return isEmpty(value) ? null : (
																	<tr
																		style={{
																			color: 'coral',
																		}}>
																		<td>{key}</td>
																		{years.map((year, i) => (
																			<td key={`demande-group-${year}-${i}`}>
																				{formatNum({
																					value: value[year] || 0,
																				})}
																			</td>
																		))}
																	</tr>
																);
															})}
														<tr>
															<td>Remaining capacity</td>
															{years.map((year, i) => {
																const d = demande[year] ?? 0;
																const c = capa[year] ?? 0;
																const diff = c - d;
																return (
																	<td
																		key={`remaining-capa-${region}-${year}-${i}`}
																		style={{
																			color: getColor(diff / d),
																		}}>
																		{formatNum({ value: diff || 0 })}
																	</td>
																);
															})}
														</tr>
														<tr>
															<td>Delta %</td>
															{years.map((year, i) => {
																const d = demande[year] ?? 0;
																const c = capa[year] ?? 0;
																const diff = c - d;

																return (
																	<td
																		key={`delta-group-${year}-${i}`}
																		style={{
																			color: getColor(diff / d),
																		}}>
																		{delta(c, d)}%
																	</td>
																);
															})}
														</tr>
														{!isProductionRegion &&
															plantExpanded[productionRegion] &&
															getPlantsByRegionAndProductionRegion(
																r,
																productionRegion
															).map((plant, plantIndex) => {
																const capa = getCapacityByYearByGlobalRegion(
																	r,
																	productionRegion,
																	plant
																);

																const demande = getDemandByYearByGlobalRegion(
																	r,
																	productionRegion,
																	plant
																);
																return isEmpty(demande) &&
																	isEmpty(capa) ? null : (
																	<React.Fragment
																		key={`${plant}-${plantIndex}`}>
																		<tr
																			style={{
																				background: 'none',
																			}}>
																			<td
																				rowSpan={
																					(isPlant &&
																					showDemandDetailsPlant[plant]
																						? Object.entries(
																								getDemandDetailByYearByGlobalRegion(
																									r,
																									productionRegion,
																									plant
																								)
																						  ).filter(
																								([_, value]) => !isEmpty(value)
																						  ).length
																						: 0) + detailsCount
																				}
																				style={{
																					position: 'relative',
																					border: 'none',
																				}}>
																				<Row
																					center
																					className='full'
																					style={{
																						border: '1px solid #ccc',
																						borderRight: 'none',
																						borderTop:
																							regionIndex === 0
																								? 'none'
																								: '1px solid #ccc',
																						borderBottom: '1px solid #ccc',
																						position: 'absolute',
																						top: 0,
																						right: 0,
																						bottom: 0,
																						width: '170px',
																					}}>
																					{plant}
																				</Row>
																			</td>
																			<td>{capacityDisplay}</td>
																			{years.map((year, i) => (
																				<td key={`capa-${plant}-${year}-${i}`}>
																					{formatNum({
																						value: capa[year] || 0,
																					})}
																				</td>
																			))}
																		</tr>
																		<tr>
																			<td>
																				<Row center className='full'>
																					<span>Demand</span>
																					{isPlant && (
																						<span
																							onClick={() => {
																								setShowDemandDetailsPlant(
																									(_) => ({
																										..._,
																										[plant]: !_[plant],
																									})
																								);
																							}}
																							style={{
																								cursor: 'pointer',
																							}}>
																							<Icon
																								icon='caret-right'
																								style={{
																									transition: 'transform 0.2s',
																									transform:
																										showDemandDetailsPlant[
																											plant
																										]
																											? 'rotate(90deg)'
																											: 'rotate(0deg)',
																								}}
																							/>
																						</span>
																					)}
																				</Row>
																			</td>
																			{years.map((year, i) => (
																				<td
																					key={`demande-${region}-${year}-${i}`}>
																					{formatNum({
																						value: demande[year] || 0,
																					})}
																				</td>
																			))}
																		</tr>
																		{isPlant &&
																			showDemandDetailsPlant[plant] &&
																			Object.entries(
																				getDemandDetailByYearByGlobalRegion(
																					r,
																					productionRegion,
																					plant
																				)
																			).map(([key, value]) => {
																				return isEmpty(value) ? null : (
																					<tr
																						style={{
																							color: 'coral',
																						}}>
																						<td>{key}</td>
																						{years.map((year, i) => (
																							<td
																								key={`demande-group-${year}-${i}`}>
																								{formatNum({
																									value: value[year] || 0,
																								})}
																							</td>
																						))}
																					</tr>
																				);
																			})}
																		<tr>
																			<td>Remaining capacity</td>
																			{years.map((year, i) => {
																				const d = demande[year] ?? 0;
																				const c = capa[year] ?? 0;
																				const diff = c - d;
																				return (
																					<td
																						key={`remaining-capa-${plant}-${year}-${i}`}
																						style={{
																							color: getColor(diff / d),
																						}}>
																						{formatNum({
																							value: diff || 0,
																						})}
																					</td>
																				);
																			})}
																		</tr>
																		<tr>
																			<td>Delta %</td>
																			{years.map((year, i) => {
																				const d = demande[year] ?? 0;
																				const c = capa[year] ?? 0;
																				const diff = c - d;

																				return (
																					<td
																						key={`delta-group-${year}-${i}`}
																						style={{
																							color: getColor(diff / d),
																						}}>
																						{delta(c, d)}%
																					</td>
																				);
																			})}
																		</tr>
																	</React.Fragment>
																);
															})}
													</React.Fragment>
												);
											}
										)}
								</React.Fragment>
							);
						})}
				</tbody>
			</Table>
			{processStep === 'ycw_cream' && (
				<Table>
					<thead>
						<tr>
							<th></th>
							<th>YCW Cream</th>
							{years.map((year) => (
								<th key={year}>{year}</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							<td rowSpan={4}>
								<Row center className='full'>
									<span>Group</span>
									<span
										onClick={() => {
											setYcwRegionsExpanded((_) => !_);
										}}
										style={{
											cursor: 'pointer',
										}}>
										{!isGroup && (
											<Icon
												icon='caret-right'
												style={{
													transition: 'transform 0.2s',
													transform: ycwRegionsExpanded
														? 'rotate(90deg)'
														: 'rotate(0deg)',
												}}
											/>
										)}
									</span>
								</Row>
							</td>
							<td>Generation</td>
							{years.map((year, i) => {
								const data = getCapacityByYearByGlobalRegion(null);
								return (
									<td key={`ywc-capa-group-${year}-${i}`}>
										{formatNum({
											value: data[year] || 0,
										})}
									</td>
								);
							})}
						</tr>
						<tr>
							<td>
								<Row center className='full'>
									<span>Drying Capacity</span>
								</Row>
							</td>
							{years.map((year, i) => {
								const data = getDryingCapacity(null);
								return (
									<td key={`demande-group-${year}-${i}`}>
										{formatNum({
											value: data[year] || 0,
										})}
									</td>
								);
							})}
						</tr>

						<tr>
							<td>Remaining</td>
							{years.map((year, i) => {
								const capa = getCapacityByYearByGlobalRegion(null);
								const dryingCapa = getDryingCapacity(null);
								const diff = (dryingCapa[year] ?? 0) - (capa[year] ?? 0);
								return (
									<td
										key={`ycw-diff-capa-${year}-${i}`}
										style={{
											color: getColor(diff / capa[year]),
										}}>
										{formatNum({
											value: diff || 0,
										})}
									</td>
								);
							})}
						</tr>
						<tr>
							<td>Delta %</td>
							{years.map((year, i) => {
								const capa = getCapacityByYearByGlobalRegion(null)[year] ?? 0;
								const dryingCapa = getDryingCapacity(null)[year] ?? 0;
								const diff = dryingCapa - capa;

								return (
									<td
										key={`delta-group-${year}-${i}`}
										style={{
											color: getColor(diff / capa),
										}}>
										{delta(dryingCapa, capa)}%
									</td>
								);
							})}
						</tr>
						{!isGroup &&
							ycwRegionsExpanded &&
							regions.map((region, regionIndex) => {
								const r = region as string;
								const isProductionRegionExpanded =
									ycwProductionRegionsExpanded[r];
								const capa = getCapacityByYearByGlobalRegion(r);
								const dryingCapa = getDryingCapacity(r);

								return isEmpty(dryingCapa) && isEmpty(capa) ? null : (
									<React.Fragment key={`ywc-${region}-${regionIndex}`}>
										<tr>
											<td
												rowSpan={4}
												style={{
													position: 'relative',
													border: 'none',
												}}>
												<Row
													center
													className='full'
													style={{
														border: '1px solid #ccc',
														borderRight: 'none',
														borderTop:
															regionIndex === 0 ? 'none' : '1px solid #ccc',
														borderBottom: '1px solid #ccc',
														position: 'absolute',
														top: 0,
														right: 0,
														bottom: 0,
														width: '190px',
													}}>
													<span>{region as string}</span>
													<span
														onClick={() => {
															setYcwProductionRegionsExpanded((_) => ({
																..._,
																[r]: !_[r],
															}));
														}}
														style={{
															cursor: 'pointer',
														}}>
														{!isGlobalRegion && (
															<Icon
																icon='caret-right'
																style={{
																	transition: 'transform 0.2s',
																	transform: isProductionRegionExpanded
																		? 'rotate(90deg)'
																		: 'rotate(0deg)',
																}}
															/>
														)}
													</span>
												</Row>
											</td>
											<td>Generation</td>
											{years.map((year, i) => {
												return (
													<td key={`ywc-capa-${region}-${year}-${i}`}>
														{formatNum({
															value: capa[year] || 0,
														})}
													</td>
												);
											})}
										</tr>
										<tr>
											<td>
												<Row center className='full'>
													<span>Drying Capacity</span>
													{isGlobalRegion && (
														<span
															onClick={() => {
																setShowDemandDetailsGlobalRegion((_) => ({
																	..._,
																	[r]: !_[r],
																}));
															}}
															style={{
																cursor: 'pointer',
															}}>
															<Icon
																icon='caret-right'
																style={{
																	transition: 'transform 0.2s',
																	transform: showDemandDetailsGlobalRegion[r]
																		? 'rotate(90deg)'
																		: 'rotate(0deg)',
																}}
															/>
														</span>
													)}
												</Row>
											</td>
											{years.map((year, i) => (
												<td key={`ywc-drying-${region}-${year}-${i}`}>
													{formatNum({
														value: dryingCapa[year] || 0,
													})}
												</td>
											))}
										</tr>

										<tr>
											<td>Remaining </td>
											{years.map((year, i) => {
												const d = dryingCapa[year] ?? 0;
												const c = capa[year] ?? 0;
												const diff = d - c;
												return (
													<td
														key={`ywc-remaining-capa-${region}-${year}-${i}`}
														style={{
															color: getColor(diff / c),
														}}>
														{formatNum({
															value: diff || 0,
														})}
													</td>
												);
											})}
										</tr>
										<tr>
											<td>Delta %</td>
											{years.map((year, i) => {
												const d = dryingCapa[year] ?? 0;
												const c = capa[year] ?? 0;
												const diff = d - c;

												return (
													<td
														key={`delta-group-${year}-${i}`}
														style={{
															color: getColor(diff / c),
														}}>
														{delta(d, c)}%
													</td>
												);
											})}
										</tr>

										{!isGlobalRegion &&
											ycwProductionRegionsExpanded[region as string] &&
											getProductionRegionsByRegion(r).map(
												(productionRegion, prodIndex) => {
													const isPlantExpanded =
														ycwPlantExpanded[productionRegion];
													const dryingCapa = getDryingCapacity(
														r,
														productionRegion
													);
													const capa = getCapacityByYearByGlobalRegion(
														r,
														productionRegion
													);
													return isEmpty(dryingCapa) && isEmpty(capa) ? null : (
														<React.Fragment key={`ywc-${region}-${prodIndex}`}>
															<tr
																style={{
																	background: 'none',
																}}>
																<td
																	rowSpan={4}
																	style={{
																		position: 'relative',
																		border: 'none',
																	}}>
																	<Row
																		center
																		className='full'
																		style={{
																			border: '1px solid #ccc',
																			borderRight: 'none',
																			borderTop:
																				prodIndex === 0
																					? 'none'
																					: '1px solid #ccc',
																			borderBottom: '1px solid #ccc',
																			position: 'absolute',
																			top: 0,
																			right: 0,
																			bottom: 0,
																			width: '180px',
																		}}>
																		<span>{productionRegion}</span>
																		<span
																			onClick={() => {
																				setYcwPlantExpanded((_) => ({
																					..._,
																					[productionRegion]:
																						!_[productionRegion],
																				}));
																			}}
																			style={{
																				cursor: 'pointer',
																			}}>
																			{!isProductionRegion && (
																				<Icon
																					icon='caret-right'
																					style={{
																						transition: 'transform 0.2s',
																						transform: isPlantExpanded
																							? 'rotate(90deg)'
																							: 'rotate(0deg)',
																					}}
																				/>
																			)}
																		</span>
																	</Row>
																</td>
																<td>Generation </td>
																{years.map((year, i) => (
																	<td key={`capa-${region}-${year}-${i}`}>
																		{formatNum({
																			value: capa[year] || 0,
																		})}
																	</td>
																))}
															</tr>
															<tr>
																<td>
																	<Row center className='full'>
																		<span>Drying Capacity</span>
																	</Row>
																</td>
																{years.map((year, i) => (
																	<td
																		key={`ycw-demande-${region}-${year}-${i}`}>
																		{formatNum({
																			value: dryingCapa[year] || 0,
																		})}
																	</td>
																))}
															</tr>

															<tr>
																<td>Remaining</td>
																{years.map((year, i) => {
																	const d = dryingCapa[year] ?? 0;
																	const c = capa[year] ?? 0;
																	const diff = d - c;
																	return (
																		<td
																			key={`ywc-remaining-capa-${region}-${year}-${i}`}
																			style={{
																				color: getColor(diff / c),
																			}}>
																			{formatNum({ value: diff || 0 })}
																		</td>
																	);
																})}
															</tr>
															<tr>
																<td>Delta %</td>
																{years.map((year, i) => {
																	const d = dryingCapa[year] ?? 0;
																	const c = capa[year] ?? 0;
																	const diff = d - c;

																	return (
																		<td
																			key={`delta-group-${year}-${i}`}
																			style={{
																				color: getColor(diff / c),
																			}}>
																			{delta(d, c)}%
																		</td>
																	);
																})}
															</tr>
															{!isProductionRegion &&
																ycwPlantExpanded[productionRegion] &&
																getPlantsByRegionAndProductionRegion(
																	r,
																	productionRegion
																).map((plant, plantIndex) => {
																	const capa = getCapacityByYearByGlobalRegion(
																		r,
																		productionRegion,
																		plant
																	);

																	const dryingCapa = getDryingCapacity(
																		r,
																		productionRegion,
																		plant
																	);
																	return isEmpty(dryingCapa) &&
																		isEmpty(capa) ? null : (
																		<React.Fragment
																			key={`ycw-${plant}-${plantIndex}`}>
																			<tr
																				style={{
																					background: 'none',
																				}}>
																				<td
																					rowSpan={4}
																					style={{
																						position: 'relative',
																						border: 'none',
																					}}>
																					<Row
																						center
																						className='full'
																						style={{
																							border: '1px solid #ccc',
																							borderRight: 'none',
																							borderTop:
																								regionIndex === 0
																									? 'none'
																									: '1px solid #ccc',
																							borderBottom: '1px solid #ccc',
																							position: 'absolute',
																							top: 0,
																							right: 0,
																							bottom: 0,
																							width: '170px',
																						}}>
																						{plant}
																					</Row>
																				</td>
																				<td>Generation</td>
																				{years.map((year, i) => (
																					<td
																						key={`ycw-capa-${plant}-${year}-${i}`}>
																						{formatNum({
																							value: capa[year] || 0,
																						})}
																					</td>
																				))}
																			</tr>
																			<tr>
																				<td>
																					<Row center className='full'>
																						<span>Drying Capacity</span>
																					</Row>
																				</td>
																				{years.map((year, i) => (
																					<td
																						key={`ycw-demande-${region}-${year}-${i}`}>
																						{formatNum({
																							value: dryingCapa[year] || 0,
																						})}
																					</td>
																				))}
																			</tr>
																			<tr>
																				<td>Remaining capacity</td>
																				{years.map((year, i) => {
																					const d = dryingCapa[year] ?? 0;
																					const c = capa[year] ?? 0;
																					const diff = d - c;
																					return (
																						<td
																							key={`ycw-remaining-capa-${plant}-${year}-${i}`}
																							style={{
																								color: getColor(diff / c),
																							}}>
																							{formatNum({
																								value: diff || 0,
																							})}
																						</td>
																					);
																				})}
																			</tr>
																			<tr>
																				<td>Delta %</td>
																				{years.map((year, i) => {
																					const d = dryingCapa[year] ?? 0;
																					const c = capa[year] ?? 0;
																					const diff = d - c;

																					return (
																						<td
																							key={`ycw-delta-group-${year}-${i}`}
																							style={{
																								color: getColor(diff / c),
																							}}>
																							{delta(d, c)}%
																						</td>
																					);
																				})}
																			</tr>
																		</React.Fragment>
																	);
																})}
														</React.Fragment>
													);
												}
											)}
									</React.Fragment>
								);
							})}
					</tbody>
				</Table>
			)}
		</Column>
	);
};

export default MatchingCapacity;
